import type { BrandLookupResult } from '@/lib/api'

export interface LookupPalette {
  accent: string | null
  card: string | null
  deep: string | null
  qualityScore: number | null
  lowConfidence: boolean
  accentSource: string | null
  cardSource: string | null
  deepSource: string | null
}

function dedupeColorCandidates(lookupResult: BrandLookupResult): string[] {
  const ordered: string[] = []
  const candidates = [
    lookupResult.primaryColor,
    lookupResult.secondaryColor || null,
    ...(lookupResult.colors || []).map((entry) => entry.hex),
  ].filter((value): value is string => Boolean(value))

  for (const color of candidates) {
    const normalized = color.toLowerCase()
    if (!ordered.some((existing) => existing.toLowerCase() === normalized)) {
      ordered.push(color)
    }
  }

  return ordered
}

function normalizeQualityScore(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || Number.isNaN(value)) return null
  return Math.max(0, Math.min(1, value))
}

export function extractLookupPalette(lookupResult: BrandLookupResult): LookupPalette {
  const qualityScore = normalizeQualityScore(lookupResult.qualityScore)
  const lowConfidence =
    typeof lookupResult.lowConfidence === 'boolean'
      ? lookupResult.lowConfidence
      : qualityScore !== null && qualityScore < 0.7

  if (lookupResult.derivedPalette) {
    return {
      accent: lookupResult.derivedPalette.accent,
      card: lookupResult.derivedPalette.card,
      deep: lookupResult.derivedPalette.deep,
      qualityScore,
      lowConfidence,
      accentSource: lookupResult.derivedPalette.accentSource || null,
      cardSource: lookupResult.derivedPalette.cardSource || null,
      deepSource: lookupResult.derivedPalette.deepSource || null,
    }
  }

  const ordered = dedupeColorCandidates(lookupResult)
  return {
    accent: ordered[0] || null,
    card: ordered[1] || null,
    deep: ordered[2] || null,
    qualityScore,
    lowConfidence,
    accentSource: null,
    cardSource: null,
    deepSource: null,
  }
}
